/**
 * Configurable option icons — single store on API (data/config-icons).
 * Served at /api/ConfigIcons/files/{filename}.
 * Optional override: REACT_APP_CONFIG_ICON_BASE_URL (full base including /ConfigIcons/files).
 */

import { NewApiBaseUrl } from '../consts/new-api-base-url'
import {
  fetchConfigIconsList,
  isApiSuccess,
  parseConfigIconsDefaultIcons,
  parseConfigIconsList,
} from '../services/config-icons-api'

export const DEFAULT_OPTION_ICON_FILENAME = 'unknown.svg'

/** Built-in icons shipped under config/default-icons (fallback when API omits catalog). */
export const BUILTIN_CONFIG_ICON_FILENAMES = [
  'bicycle.svg',
  'bus.svg',
  'car.svg',
  'moped.svg',
  'motorcycle.svg',
  'pedestrian.svg',
  'person.svg',
  'ridden-animal.svg',
  'road.svg',
  'tractor.svg',
  'unknown.svg',
  'van.svg',
]

/** @type {string[] | null} */
let cachedManifest = null

/** @type {string[] | null} */
let cachedDefaultIcons = null

function getApiIconBaseUrl() {
  const configured = process.env.REACT_APP_CONFIG_ICON_BASE_URL
  if (configured) {
    return configured.replace(/\/$/, '')
  }
  const apiRoot = String(NewApiBaseUrl).replace(/\/$/, '')
  return `${apiRoot}/ConfigIcons/files`
}

/**
 * @returns {string}
 */
export function getConfigOptionIconBaseUrl() {
  return getApiIconBaseUrl()
}

/**
 * @param {string | null | undefined} filename
 * @returns {string | null}
 */
export function getConfigOptionIconUrl(filename) {
  const safe = sanitizeIconFilename(filename)
  if (!safe) return null
  return `${getConfigOptionIconBaseUrl()}/${safe}`
}

/**
 * Normalize user input to symbolic base name (lowercase, hyphen-separated).
 * @param {string | null | undefined} input
 * @returns {string}
 */
export function normalizeSymbolicIconName(input) {
  if (typeof input !== 'string') return ''
  let normalized = input.trim().toLowerCase()
  normalized = normalized.replace(/[_\s]+/g, '-')
  normalized = normalized.replace(/[^a-z0-9-]+/g, '-')
  normalized = normalized.replace(/-{2,}/g, '-')
  normalized = normalized.replace(/^-+|-+$/g, '')
  return normalized
}

/**
 * @param {string | null | undefined} symbolicName
 * @returns {string | null}
 */
export function symbolicNameToFilename(symbolicName) {
  const base = normalizeSymbolicIconName(symbolicName)
  if (!base || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(base)) return null
  return `${base}.svg`
}

/**
 * @param {string | null | undefined} filename
 * @returns {string | null}
 */
export function sanitizeIconFilename(filename) {
  if (typeof filename !== 'string') return null
  const trimmed = filename.trim()
  if (!trimmed || trimmed.includes('..') || trimmed.includes('/') || trimmed.includes('\\')) {
    return null
  }
  if (!/^[a-z0-9]+(-[a-z0-9]+)*\.svg$/.test(trimmed)) return null
  return trimmed
}

export function clearConfigOptionIconManifestCache() {
  cachedManifest = null
  cachedDefaultIcons = null
}

/**
 * @param {string | null | undefined} filename
 * @returns {boolean}
 */
export function isDefaultConfigIcon(filename) {
  const safe = sanitizeIconFilename(filename)
  if (!safe) return false
  const defaults = cachedDefaultIcons ?? BUILTIN_CONFIG_ICON_FILENAMES
  return defaults.includes(safe)
}

/**
 * Persisted icon value for a structure option (never empty).
 * @param {string | null | undefined} filename
 * @returns {string}
 */
export function normalizeStoredOptionIcon(filename) {
  return sanitizeIconFilename(filename) || DEFAULT_OPTION_ICON_FILENAME
}

/**
 * @param {string} [token]
 * @returns {Promise<string[]>}
 */
export async function fetchConfigOptionIconManifest(token) {
  if (cachedManifest) return cachedManifest

  try {
    const { status, body } = await fetchConfigIconsList(token || '')
    if (status >= 200 && status < 300 && isApiSuccess(body)) {
      const defaults = parseConfigIconsDefaultIcons(body)
        .map((item) => sanitizeIconFilename(String(item)))
        .filter(Boolean)
      if (defaults.length > 0) {
        cachedDefaultIcons = defaults
      }

      const list = parseConfigIconsList(body)
        .map((item) => sanitizeIconFilename(String(item)))
        .filter(Boolean)
      if (list.length > 0) {
        cachedManifest = list
        return cachedManifest
      }
    }
  } catch {
    /* fall through to built-in defaults */
  }

  cachedDefaultIcons = [...BUILTIN_CONFIG_ICON_FILENAMES]
  cachedManifest = [...BUILTIN_CONFIG_ICON_FILENAMES]
  return cachedManifest
}

/**
 * Default icon filename from label-based kind (map popup fallback).
 * @param {string | null | undefined} kind
 * @returns {string | null}
 */
export function defaultIconFilenameForKind(kind) {
  switch (kind) {
    case 'cyclist':
      return 'bicycle.svg'
    case 'pedestrian':
      return 'pedestrian.svg'
    case 'motorcycle':
      return 'motorcycle.svg'
    case 'moped':
      return 'moped.svg'
    case 'bus':
      return 'bus.svg'
    case 'van':
      return 'van.svg'
    case 'car':
      return 'car.svg'
    case 'person':
      return 'person.svg'
    case 'ridden-animal':
      return 'ridden-animal.svg'
    case 'road':
      return 'road.svg'
    case 'tractor':
      return 'tractor.svg'
    default:
      return DEFAULT_OPTION_ICON_FILENAME
  }
}

/** Canonical Unit Type labels → default icon (structure.json). */
const UNIT_TYPE_DEFAULT_ICONS = {
  Pedestrian: 'pedestrian.svg',
  Bicycle: 'bicycle.svg',
  Motorcycle: 'motorcycle.svg',
  Moped: 'moped.svg',
  'Electric or kick scooter': 'moped.svg',
  'Three wheeler': 'car.svg',
  Car: 'car.svg',
  Minibus: 'van.svg',
  Bus: 'bus.svg',
  'Goods vehicle under 3.5t mgw': 'van.svg',
  'Goods vehicle over 3.5t mgw': 'van.svg',
  'Road tractor': 'van.svg',
  'Agricultural tractor': 'tractor.svg',
  'Ridden animal': 'ridden-animal.svg',
  'Other motor vehicle': 'car.svg',
  'Other light vehicle': 'car.svg',
}

/**
 * Default icon for a Unit Type option canonical label.
 * @param {string | null | undefined} canonicalLabel
 * @returns {string | null}
 */
export function defaultIconFilenameForUnitTypeLabel(canonicalLabel) {
  if (typeof canonicalLabel !== 'string') return null
  const trimmed = canonicalLabel.trim()
  return UNIT_TYPE_DEFAULT_ICONS[trimmed] ?? null
}

/**
 * Effective icon for a select option (configured icon or field-specific default).
 * @param {string} fieldLabel
 * @param {{ label?: string, icon?: string } | null | undefined} option
 * @returns {string | null}
 */
export function resolveDefaultOptionIconFilename(fieldLabel, option) {
  if (!option) return null
  const configured = sanitizeIconFilename(typeof option.icon === 'string' ? option.icon : null)
  const isGenericDefault = configured === DEFAULT_OPTION_ICON_FILENAME

  // Explicit custom icon (admin override) — not the auto generic default.
  if (configured && !isGenericDefault) return configured

  if (fieldLabel === 'Type') {
    const unitTypeIcon = defaultIconFilenameForUnitTypeLabel(option.label)
    if (unitTypeIcon) return unitTypeIcon
  }

  return configured || DEFAULT_OPTION_ICON_FILENAME
}

/**
 * @param {Record<string, Array<{ value: unknown, label: string, icon?: string }>>} configOptions
 * @param {string} fieldLabel
 * @param {unknown} value
 * @returns {string | null}
 */
export function resolveOptionIconFilename(configOptions, fieldLabel, value) {
  if (value == null || value === '') return null
  const options = configOptions?.[fieldLabel]
  if (!Array.isArray(options)) return null
  const match = options.find(
    (opt) => opt.value === value || Number(opt.value) === Number(value),
  )
  if (!match) return null
  return resolveDefaultOptionIconFilename(fieldLabel, match)
}

/**
 * @param {string | null | undefined} symbolicName
 * @returns {boolean}
 */
export function isValidSymbolicIconName(symbolicName) {
  const base = normalizeSymbolicIconName(symbolicName)
  return Boolean(base && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(base))
}
